import { defineBlock, EditableText } from "eddev/blocks"
import { useHydrating } from "eddev/routing"
import { Col, Container, Grid } from "@components/layout/Grid"
import { cn, tv } from "@utils/tw"
import { ComponentPropsWithoutRef, forwardRef, useState } from "react"
import { Typography } from "@components/atoms/Typography"
import { useIsMobile } from "@hooks/useViewportDerived"
import { Heading } from "@components/atoms/Heading"
import { Button } from "@components/atoms/Button"
import { ButtonSelections } from "@components/molecules/archive/ButtonSelections"
import { SelectedFiltersT, TaxonomyT } from "@components/molecules/archive/types"
import { SortButtonWrapper } from "@components/molecules/archive/SortButtonWrapper"
import { useProjectTotals, useProjects } from "@hooks/queries"
import { AnimateRow } from "@components/molecules/archive/AnimateRow"
import { Loader, SelectedFiltersChips, Showing } from "@components/atoms/archive/Components"
import { OrderEnum, PostObjectsConnectionOrderbyEnum } from "../../types.graphql"
import { Chip } from "@components/atoms/Chip"
import { ResponsiveImage } from "@components/atoms/ResponsiveImage"

export const meta: BlockMeta = {
  title: "Projects",
  category: "common",
  icon: "hammer",
  keywords: ["pill list"],
  tags: ["root"],
  editMode: "preview",
  lazyLoad: false,
  conditions: {
    postTypes: ["page", "post"],
  },
}

export default defineBlock("general/projects", (props) => {
  const isMobile = useIsMobile()
  const hydrating = useHydrating()
  const [listView, setListView] = useState<boolean>(false)
  const [filtersOpen, setFiltersOpen] = useState<boolean>(false)
  const [selectedFilters, setSelectedFilters] = useState<SelectedFiltersT>({
    focusAreas: props.rsrpFocusAreas?.nodes.filter((node) => props.prefilterFocusArea?.includes(node.slug)) ?? [],
    tags: [],
    sort: {
      name: "Latest first",
      uri: "latest-first",
      slug: "latest-first",
    },
  })
  const [dropDownOpen, setDropDownOpen] = useState<"focus" | "tags" | "sort" | null>(null)

  const sort: TaxonomyT[] = [
    {
      name: "Latest first",
      uri: "latest-first",
      slug: "latest-first",
    },
    {
      name: "Oldest first",
      uri: "oldest-first",
      slug: "oldest-first",
    },
    {
      name: "A - Z",
      uri: "a-z",
      slug: "a-z",
    },
  ]

  const totalProjects = useProjectTotals(undefined).data?.rsrpProjects?.nodes
  const projects = useProjects({
    projectFocusAreaSlug:
      selectedFilters.focusAreas!.length > 0 ? selectedFilters.focusAreas?.map((area) => area.slug) : null,
    projectTopicSlug: selectedFilters.tags!.length > 0 ? selectedFilters.tags?.map((tag) => tag.slug) : null,
    field:
      selectedFilters.sort.slug === "latest-first"
        ? PostObjectsConnectionOrderbyEnum.Date
        : selectedFilters.sort.slug === "oldest-first"
          ? PostObjectsConnectionOrderbyEnum.Date
          : PostObjectsConnectionOrderbyEnum.Title,
    order:
      selectedFilters.sort.slug === "latest-first"
        ? OrderEnum.Desc
        : selectedFilters.sort.slug === "oldest-first"
          ? OrderEnum.Asc
          : OrderEnum.Asc,
  })

  return (
    <>
      {!hydrating && totalProjects && (
        <ProjectContainer>
          <Grid>
            <Col initial={12}>
              <EditableText
                as="h2"
                className="!type-title-l !text-center"
                store="title"
                placeholder="Enter title"
                defaultValue="Projects"
              />
            </Col>
            <Col initial={8}>
              <Filters open={filtersOpen}>
                {props.rsrpFocusAreas && !props.prefilterFocusArea && (
                  <div>
                    <Button
                      color="grey"
                      size={isMobile ? "large" : "small"}
                      icon={dropDownOpen === "focus" ? "CARET_UP" : "CARET_DOWN"}
                      iconStyle="right"
                      onClick={() => setDropDownOpen(dropDownOpen === "focus" ? null : "focus")}
                    >
                      Focus
                    </Button>
                    <FilterDropdown open={dropDownOpen === "focus"}>
                      <ul>
                        {props.rsrpFocusAreas.nodes?.map((focus, i) => (
                          <ButtonSelections
                            taxonomy={focus}
                            type="focusAreas"
                            style="button"
                            selectedFilters={selectedFilters}
                            setSelectedFilters={setSelectedFilters}
                            key={i}
                          />
                        ))}
                      </ul>
                    </FilterDropdown>
                  </div>
                )}

                {props.rsrpTopics && (
                  <div>
                    <Button
                      color="grey"
                      size={isMobile ? "large" : "small"}
                      icon={dropDownOpen === "tags" ? "CARET_UP" : "CARET_DOWN"}
                      iconStyle="right"
                      onClick={() => setDropDownOpen(dropDownOpen === "tags" ? null : "tags")}
                    >
                      Tags
                    </Button>
                    <FilterDropdown open={dropDownOpen === "tags"}>
                      <ul>
                        {props.rsrpTopics.nodes?.map((tag, i) => (
                          <ButtonSelections
                            taxonomy={tag}
                            type="tags"
                            style="button"
                            selectedFilters={selectedFilters}
                            setSelectedFilters={setSelectedFilters}
                            key={i}
                          />
                        ))}
                      </ul>
                    </FilterDropdown>
                  </div>
                )}

                <div>
                  <Button
                    color="grey"
                    size={isMobile ? "large" : "small"}
                    icon={dropDownOpen === "sort" ? "CARET_UP" : "CARET_DOWN"}
                    iconStyle="right"
                    onClick={() => setDropDownOpen(dropDownOpen === "sort" ? null : "sort")}
                  >
                    Sort by
                  </Button>
                  <FilterDropdown open={dropDownOpen === "sort"}>
                    <ul>
                      {sort.map((opt, i) => (
                        <SortButtonWrapper
                          taxonomy={opt}
                          selectedFilters={selectedFilters}
                          setSelectedFilters={setSelectedFilters}
                          key={i}
                        />
                      ))}
                    </ul>
                  </FilterDropdown>
                </div>
              </Filters>
            </Col>
            <Col initial={4}>
              <ToggleView>
                <Button
                  size={isMobile ? "large" : "tiny"}
                  color={listView ? "white" : "grey"}
                  icon="GRID"
                  iconStyle="left"
                  onClick={() => setListView(false)}
                />
                <Button
                  size={isMobile ? "large" : "tiny"}
                  color={listView ? "grey" : "white"}
                  icon="LIST"
                  iconStyle="left"
                  onClick={() => setListView(true)}
                />
              </ToggleView>
            </Col>

            {projects.data?.rsrpProjects?.nodes ? (
              <>
                <Col initial={12} tablet={6} laptop={4} className="py-4">
                  <Showing>
                    Showing {projects.data.rsrpProjects.nodes.length} of {totalProjects.length} projects
                  </Showing>
                </Col>
                <Col initial={12} tablet={6} laptop={8} className="py-4">
                  <SelectedFiltersChips>
                    {selectedFilters?.tags?.map((tax, i) => (
                      <ButtonSelections
                        taxonomy={tax}
                        type="tags"
                        style="chip"
                        selectedFilters={selectedFilters}
                        setSelectedFilters={setSelectedFilters}
                        key={i}
                      />
                    ))}
                    {!props.prefilterFocusArea &&
                      selectedFilters?.focusAreas?.map((cat, i) => (
                        <ButtonSelections
                          taxonomy={cat}
                          type="focusAreas"
                          style="chip"
                          selectedFilters={selectedFilters}
                          setSelectedFilters={setSelectedFilters}
                          key={i}
                        />
                      ))}
                  </SelectedFiltersChips>
                </Col>
                {projects.isLoading && <Loader listView={listView ? true : false} />}
                {projects.data.rsrpProjects.nodes.length === 0 ? (
                  <Col initial={12}>
                    <NoResults>Your filters have returned no results. Please try changing your filters.</NoResults>
                  </Col>
                ) : (
                  <>
                    {listView ? (
                      <Col initial={12} className="min-h-[500px]">
                        <ul>
                          {projects.data.rsrpProjects.nodes.map((project, i) => (
                            <li key={i}>
                              <AnimateRow article={project} order={i} />
                            </li>
                          ))}
                        </ul>
                      </Col>
                    ) : (
                      <>
                        {projects.data.rsrpProjects.nodes.map((project, i) => (
                          <Col initial={12} desktop={6} key={i}>
                            <ProjectCard>
                              <Left>
                                <Top>
                                  <Chips>
                                    {project.rsrpFocusAreas?.nodes.map((tax, k) => (
                                      <Chip color="outline" {...tax} key={k}>
                                        {tax.name}
                                      </Chip>
                                    ))}
                                    {project.rsrpTopics?.nodes.map((tax, k) => (
                                      <Chip color="outline" {...tax} key={k}>
                                        {tax.name}
                                      </Chip>
                                    ))}
                                  </Chips>

                                  <Heading as="h3" variant="h2">
                                    {project.title}
                                  </Heading>

                                  <Typography
                                    as="p"
                                    variant="text-m"
                                    dangerouslySetInnerHTML={{ __html: project.excerpt ? project.excerpt : "" }}
                                  />
                                </Top>

                                <Button
                                  color="yellow"
                                  size="medium"
                                  href={project.uri as string}
                                  icon="ARROW_RIGHT"
                                  iconStyle="right"
                                >
                                  View project
                                </Button>
                              </Left>
                              <Right>
                                {project.projectMeta?.projectDetails?.leads && (
                                  <div>
                                    <label>
                                      Lead research organisation
                                      {project.projectMeta.projectDetails.leads.length > 1 ? "s" : ""}
                                    </label>
                                    {project.projectMeta.projectDetails.leads.map((lead, k) => (
                                      <div key={k}>
                                        {lead?.logo && (
                                          <ImageContainer>
                                            <ResponsiveImage {...lead.logo} objectFit="contain" />
                                          </ImageContainer>
                                        )}
                                        <span>{lead?.name}</span>
                                      </div>
                                    ))}
                                  </div>
                                )}

                                {project.projectMeta?.projectDetails?.additional && (
                                  <div>
                                    <label>
                                      Additional research organisation
                                      {project.projectMeta.projectDetails.additional.length > 1 ? "s" : ""}
                                    </label>
                                    {project.projectMeta.projectDetails.additional.map((lead, k) => (
                                      <span key={k}>{lead?.name}</span>
                                    ))}
                                  </div>
                                )}

                                {project.projectMeta?.projectDetails?.leadResearchers && (
                                  <div>
                                    <label>
                                      Lead Researcher
                                      {project.projectMeta.projectDetails.leadResearchers.length > 1 ? "s" : ""}
                                    </label>
                                    {project.projectMeta.projectDetails.leadResearchers.map((lead, k) => (
                                      <span key={k}>{lead?.name}</span>
                                    ))}
                                  </div>
                                )}

                                {project.projectMeta?.projectDetails?.grantAmount && (
                                  <div>
                                    <label>Grant amount</label>
                                    <span>{project.projectMeta.projectDetails.grantAmount}</span>
                                  </div>
                                )}

                                {project.projectMeta?.projectDetails?.lengthOfProject && (
                                  <div>
                                    <label>Length of project</label>
                                    <span>{project.projectMeta.projectDetails.lengthOfProject}</span>
                                  </div>
                                )}

                                {project.projectMeta?.projectDetails?.inProgress ? (
                                  <>
                                    {project.projectMeta.projectDetails?.estimatedCompletionDate ? (
                                      <div>
                                        <label>Estimated completion date</label>
                                        <span>{project.projectMeta.projectDetails.estimatedCompletionDate}</span>
                                      </div>
                                    ) : (
                                      <div>
                                        <label>Current status</label>
                                        <span>In progress</span>
                                      </div>
                                    )}
                                  </>
                                ) : (
                                  <>
                                    {project.projectMeta?.projectDetails?.completionDate && (
                                      <div>
                                        <label>Completion date</label>
                                        <span>{project.projectMeta?.projectDetails.completionDate}</span>
                                      </div>
                                    )}
                                  </>
                                )}
                              </Right>
                            </ProjectCard>
                          </Col>
                        ))}
                      </>
                    )}
                  </>
                )}
              </>
            ) : (
              <Loader listView={listView ? true : false} />
            )}
          </Grid>
        </ProjectContainer>
      )}
    </>
  )
})

const ProjectContainer = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<typeof Container>>(
  ({ className, ...props }, ref) => (
    <Container
      ref={ref}
      className={cn(
        "py-6",
        "[&_h2]:text-center [&_h2]:w-full [&_h2]:block [&_h2]:mt-5 [&_h2]:mb-8",
        "[&_ul]:list-none [&_ul]:p-0 [&_ul]:m-0 [&_ul_a]:no-underline [&_ul_a]:text-black",
        "[&_.--grid]:gap-[var(--tw-grid-gutter)]",
        className,
      )}
      {...props}
    />
  ),
)
ProjectContainer.displayName = "ProjectContainer"

const ToggleView = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("w-full flex items-center justify-end gap-x-3 [&_button_span]:hidden", className)}
    {...props}
  />
))
ToggleView.displayName = "ToggleView"

const filtersStyles = tv({
  base: [
    "h-auto overflow-hidden flex flex-col transition-all ease-movement duration-300",
    "[&>div>button]:w-full [&>div>button]:mb-3 [&>div>button]:py-0 [&>div>button]:min-h-[calc(var(--spacing-7)*2)]",
    "[&>div>button_span]:grow [&>div>button_span]:text-left",
    "md:flex-row md:overflow-visible md:relative md:gap-x-4 md:max-h-none",
    "md:[&>div>button]:w-auto md:[&>div>button]:mb-0 md:[&>div>button]:min-h-[calc(var(--spacing-5)*2)]",
    "md:[&>div>div]:absolute md:[&>div>div]:top-[calc(100%+var(--spacing-3))] md:[&>div>div]:z-10",
    "lg:[&>div>button]:min-h-[calc(var(--spacing-4)*2)]",
  ],
  variants: {
    open: {
      true: "max-h-none",
      false: "max-h-0",
    },
  },
  defaultVariants: {
    open: false,
  },
})

const Filters = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div"> & { open?: boolean }>(
  ({ open, className, ...props }, ref) => <div ref={ref} className={filtersStyles({ open, className })} {...props} />,
)
Filters.displayName = "Filters"

const filterDropdownStyles = tv({
  base: [
    "block h-auto overflow-hidden transition-all ease-movement duration-300",
    "[&_ul]:w-full [&_ul]:flex [&_ul]:flex-col [&_ul]:rounded-[6px] [&_ul]:bg-light-grey [&_ul]:border [&_ul]:border-white [&_ul]:mb-6",
    "[&_ul_li]:py-1 [&_ul_li]:px-4",
    "[&_ul_li:first-of-type]:mt-4 [&_ul_li:last-of-type]:mb-4",
    "[&_ul_li_button]:w-full [&_ul_li_button_span]:grow [&_ul_li_button_span]:text-left",
    "[&_ul_li_button_svg]:text-dark-grey [&_ul_li_button_svg]:scale-[0.6]",
    "md:[&_ul_li]:py-1 md:[&_ul_li]:px-3",
    "md:[&_ul_li:first-of-type]:mt-3 md:[&_ul_li:last-of-type]:mb-3",
    "md:[&_ul_li_button]:p-0 md:[&_ul_li_button]:min-h-[calc(var(--spacing-5)*2)]",
    "md:[&_ul_li_button_span]:pr-4",
    "lg:[&_ul_li]:py-1 lg:[&_ul_li]:px-2",
    "lg:[&_ul_li:first-of-type]:mt-1 lg:[&_ul_li:last-of-type]:mb-1",
    "lg:[&_ul_li_button]:min-h-[calc(var(--spacing-4)*2)]",
  ],
  variants: {
    open: {
      true: "max-h-[100vh]",
      false: "max-h-0",
    },
  },
  defaultVariants: {
    open: false,
  },
})

const FilterDropdown = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div"> & { open?: boolean }>(
  ({ open, className, ...props }, ref) => (
    <div ref={ref} className={filterDropdownStyles({ open, className })} {...props} />
  ),
)
FilterDropdown.displayName = "FilterDropdown"

// This card doesn't use CARD_HOVER, matching the previous (pre-Tailwind) markup,
// which composed CardStyles alone (no CardHover) with its own overrides below.
// "gap-6" here overrides the base "gap-4" (see components/molecules/Cards.tsx
// for the same base string).
const ProjectCard = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "w-full flex rounded-md relative overflow-hidden bg-bg transition-[background-color] ease-movement duration-300 [&_img]:transition-transform [&_img]:ease-movement [&_img]:duration-300 [&_video]:transition-transform [&_video]:ease-movement [&_video]:duration-300 h-full border border-grey py-6 px-7 gap-6",
      className,
    )}
    {...props}
  />
))
ProjectCard.displayName = "ProjectCard"

const Left = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "w-1/2 flex flex-col justify-between items-start",
      "md:w-[calc(100%*(3/5))] lg:w-[calc(100%*(4/5))] xl:w-[calc(100%*(3/5))]",
      className,
    )}
    {...props}
  />
))
Left.displayName = "Left"

const Right = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "bg-light-grey p-4 rounded-md w-1/2 flex flex-col gap-4",
      "[&>div]:flex [&>div]:flex-col",
      "[&>div_.image-wrapper]:w-[64px] [&>div_.image-wrapper]:h-[64px] [&>div_.image-wrapper]:bg-transparent",
      "[&>div_label]:type-label-caps [&>div_span]:type-body-s",
      "md:w-[calc(100%*(2/5))]",
      className,
    )}
    {...props}
  />
))
Right.displayName = "Right"

const ImageContainer = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("w-[64px] h-[64px]", className)} {...props} />
))
ImageContainer.displayName = "ImageContainer"

const Top = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("flex flex-col gap-4 [&_h3]:mt-5 [&_h3]:mb-2 [&>p]:mb-7", className)} {...props} />
))
Top.displayName = "Top"

const Chips = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("flex gap-2 mb-4", className)} {...props} />
))
Chips.displayName = "Chips"

const NoResults = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("min-h-[500px] type-title-m", className)} {...props} />
))
NoResults.displayName = "NoResults"
